import { useEffect, useMemo, useState } from "react";
import AxiosInstance from "../Home/AxiosInstance";
import { useNavigate } from "react-router-dom";
import {useTable, useGlobalFilter, useSortBy, useResizeColumns} from "react-table";
import { FiEdit, FiTrash } from "react-icons/fi";
import TrainerHeader from "./TrainerHeader";
import Footer from "../Home/Footer";
import "./TrainerDashboard.css"
import "./trainerPortal.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

export default function TrainerDashboard() {
  const [trainerId, setTrainerId] = useState(null);
  const [courseMaterials, setCourseMaterials] = useState([]);
  const [CourseMaterialFilter, setCourseMaterialFilter] = useState("");
  const [editingCourseMaterial, setEditingCourseMaterial] = useState(null);
  const [editedCourseMaterial, setEditedCourseMaterial] = useState({});
  const [profiles, setProfiles] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const storedTrainerId = localStorage.getItem("trainerId");
    setTrainerId(storedTrainerId ? parseInt(storedTrainerId) : 4);
  }, []);

  const fetchCourseMaterials = async () => {
    try {
      const res = await AxiosInstance.get(`companies/trainer/courses/${trainerId}/`);
      setCourseMaterials(res.data);
    } catch (err) {
      console.error("Error fetching course materials:", err);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [profilesRes, coursesRes] = await Promise.all([
        AxiosInstance.get(`companies/trainer/profile/${trainerId}/`),
        AxiosInstance.get("companies/courses/"),
      ]);
      setProfiles(profilesRes.data);
      setCourseOptions(coursesRes.data);
    } catch (err) {
      console.error("Error fetching dropdown data:", err);
    }
  };

  useEffect(() => {
    if (trainerId) {
      fetchCourseMaterials();
      fetchDropdownData();
    }
  }, [trainerId]);

  const handleSaveCourseMaterial = async () => {
    try {
      const payload = {
        ...editedCourseMaterial,
        trainer: trainerId,
        course: editedCourseMaterial.course,
        profile: editedCourseMaterial.profile,
      };

      await AxiosInstance.put(
        `companies/courses/${editedCourseMaterial.course_material_id}/`,
        payload
      );
      setEditingCourseMaterial(null);
      fetchCourseMaterials();
    } catch (err) {
      console.error("Error saving course material:", err);
    }
  };

  const handleDeleteCourseMaterial = async (courseMaterialId) => {
    if (!window.confirm("Are you sure you want to delete this course material?")) return;
    try {
      await AxiosInstance.delete(`companies/course/${courseMaterialId}/`);
      setCourseMaterials((prev) => prev.filter((m) => m.course_material_id !== courseMaterialId));
    } catch (err) {
      console.error("Error deleting course material:", err);
    }
  };

  const columns = useMemo(() => [
    {
      Header: "Course Name",
      accessor: "course.course_name",
      id: "course_name",
      Cell: ({ row }) => {
        const courseName = row.original.course?.course_name || "N/A";
        const courseMaterialId = row.original.course_material_id;

        const handleClick = () => {
          localStorage.setItem("selectedCourseMaterialId", courseMaterialId);
          navigate(`/course-details/${courseMaterialId}`);
        };

        return (
          <span
            onClick={handleClick}
            style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
          >
            {courseName}
          </span>
        );
      },
    },
    {
      Header: "Profile",
      accessor: (row) => row.profile?.profile_name || "N/A",
      id: "profile_name",
    },
    { Header: "Sub Topics", accessor: "sub_topics" },
    { Header: "Description", accessor: "description" },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => {
              const original = row.original;
              setEditingCourseMaterial(original);
              setEditedCourseMaterial({
                ...original,
                course: original.course?.course_id || "",
                profile: original.profile?.profile_id || "",
              });
            }}
          >
            <FiEdit />
          </button>
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={() => handleDeleteCourseMaterial(row.original.course_material_id)}
          >
            <FiTrash />
          </button>
        </div>
      ),
    },
  ], [navigate]);

  const data = useMemo(() => courseMaterials, [courseMaterials]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable({ columns, data }, useGlobalFilter, useSortBy, useResizeColumns);

  useEffect(() => {
    setGlobalFilter(CourseMaterialFilter);
  }, [CourseMaterialFilter, setGlobalFilter]);

  return (
    <div className="TrainerPage als-trainer-portal">
      <TrainerHeader />
      <div className="trainer-container py-4">
        {/* Course Section */}
        <h2 className="section-heading">Courses</h2>
        <div className="card-custom">
        <div className="d-flex justify-content-between mb-3">
          <input
            type="text"
            className="form-control w-50"
            placeholder="Search course materials..."
            value={CourseMaterialFilter}
            onChange={(e) => setCourseMaterialFilter(e.target.value)}
          />
          <button className="add-button" onClick={() => navigate("/trainer-course-materials")}>
            Add
          </button>
        </div>

        <div className="table-responsive">
          <table {...getTableProps()} className="table table-bordered table-striped">
            <thead className="table-light">
              {headerGroups.map((group) => (
                <tr {...group.getHeaderGroupProps()} key={group.id}>
                  {group.headers.map((column) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())} key={column.id}>
                      {column.render("Header")}
                      {column.isSorted ? (column.isSortedDesc ? " ▼" : " ▲") : ""}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length}>No course materials found.</td>
                </tr>
              ) : (
                rows.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={row.id}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} key={cell.column.id}>
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {editingCourseMaterial && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Course Material</h5>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label>Profile</label>
                    <select
                      className="form-select"
                      value={editedCourseMaterial.profile || ""}
                      onChange={(e) =>
                        setEditedCourseMaterial({ ...editedCourseMaterial, profile: parseInt(e.target.value) })
                      }
                    >
                      <option value="">Select Profile</option>
                      {profiles.map((profile) => (
                        <option key={profile.profile_id} value={profile.profile_id}>
                          {profile.profile_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label>Course</label>
                    <select
                      className="form-select"
                      value={editedCourseMaterial.course || ""}
                      onChange={(e) =>
                        setEditedCourseMaterial({ ...editedCourseMaterial, course: parseInt(e.target.value) })
                      }
                    >
                      <option value="">Select Course</option>
                      {courseOptions.map((courseOption) => (
                        <option key={courseOption.course_id} value={courseOption.course_id}>
                          {courseOption.course_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label>Sub Topics</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editedCourseMaterial.sub_topics || ""}
                      onChange={(e) =>
                        setEditedCourseMaterial({ ...editedCourseMaterial, sub_topics: e.target.value })
                      }
                    />
                  </div>

                  <div className="mb-3">
                    <label>Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={editedCourseMaterial.description || ""}
                      onChange={(e) =>
                        setEditedCourseMaterial({ ...editedCourseMaterial, description: e.target.value })
                      }
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setEditingCourseMaterial(null)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveCourseMaterial}>
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
      <Footer />
    </div>
  );
}
